import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "clutch/src/Button/Button.jsx";
import { Dropdown } from "clutch/src/Dropdown/Dropdown.jsx";

import {
  cssInputError,
  Form,
} from "@/feature-auth/components/EditUserSettingsModal.style.jsx";
import { GENDER_OPTIONS } from "@/feature-auth/constants/constants.mjs";
import { updateUserInfo } from "@/feature-auth/fetches/update-user.mjs";
import MessageIcon from "@/inline-assets/settings-message.svg";
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogWindow,
} from "@/shared/Dialog.jsx";
import { classNames } from "@/util/class-names.mjs";
import { devWarn } from "@/util/dev.mjs";
import {
  getDaysInMonth,
  getLastNumberOfYears,
  getMonthNames,
} from "@/util/dob-dates.mjs";
import { findRegionCode, getRegions } from "@/util/regions.mjs";
import { isValidAge } from "@/util/validate-age.mjs";

// TODO: i18n - location and date don't localize/DMY vs MDY format?/other year era?
const EditUserSettingsModal = ({ user, close, setSnackbarText }) => {
  const { t } = useTranslation();

  const { birthday, selectedLanguage, gender, location, name } = user;

  const savedBirthday = useMemo(() => {
    let date = birthday;
    if (date && typeof date !== "string") {
      date = date.toISOString();
    }

    if (!date || date?.length < 10) return "";

    const [year, month, day] = date.substring(0, 10).split("-");
    return {
      day: Number(day),
      month: month - 1,
      year: year,
    };
  }, [birthday]);

  const months = useMemo(() => {
    return getMonthNames();
  }, []);

  const initialMonth = useMemo(
    () =>
      months.filter((month) => month.value === savedBirthday.month)[0]?.value ||
      undefined,
    [savedBirthday.month, months],
  );

  const years = useMemo(() => {
    return getLastNumberOfYears(100);
  }, []);

  const initialYear = useMemo(
    () =>
      years.filter((year) => year.label === savedBirthday.year)[0]?.value ||
      undefined,
    [savedBirthday.year, years],
  );

  const [year, setYear] = useState(initialYear);
  const [month, setMonth] = useState(initialMonth);

  const days = useMemo(() => {
    const birthdayDate = birthday ? new Date() : new Date();
    return getDaysInMonth(
      year || birthdayDate.getFullYear(),
      month || birthdayDate.getMonth(),
    );
  }, [birthday, month, year]);

  const initialDay = useMemo(
    () =>
      days.filter((day) => day.value === savedBirthday.day)[0]?.value ||
      undefined,
    [savedBirthday.day, days],
  );

  const [day, setDay] = useState(initialDay);

  const regions = useMemo(() => {
    return getRegions(selectedLanguage);
  }, [selectedLanguage]);

  const initialRegion = useMemo(() => {
    if (location?.length === 2) return location;
    return findRegionCode(location, selectedLanguage);
  }, [location, selectedLanguage]);

  const [region, setRegion] = useState(initialRegion || undefined);

  const genderOptions = useMemo(
    () =>
      GENDER_OPTIONS.map((option) => {
        return { value: option.value, label: t(...option.label) };
      }),
    [t],
  );

  const initialGenderSelection = useMemo(() => {
    const selection = genderOptions.filter(
      (option) => option.value === gender,
    )[0]?.value;
    if (selection) return selection;
    return selection === null ? null : undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gender]);

  const [selectedGender, setGender] = useState(initialGenderSelection);

  const isInAgeRange = useMemo(() => {
    return isValidAge(year, month, day);
  }, [year, month, day]);

  const showError = useMemo(
    () => (isInAgeRange === false ? "shown" : ""),
    [isInAgeRange],
  );

  const ageError =
    isInAgeRange === false
      ? t(
          "common:account.tooYoungError",
          "You must be 13 years of age or older!",
        )
      : false;

  const allFieldsAreFilled = useMemo(() => {
    return month && day && year && region && selectedGender !== undefined;
  }, [month, day, year, region, selectedGender]);

  const fieldsHaveChanged = useMemo(() => {
    return !(
      month === initialMonth &&
      Number(day) === Number(initialDay) &&
      Number(year) === Number(initialYear) &&
      region === initialRegion &&
      selectedGender === initialGenderSelection
    );
  }, [
    day,
    initialDay,
    month,
    initialMonth,
    year,
    initialYear,
    region,
    initialRegion,
    selectedGender,
    initialGenderSelection,
  ]);
  const btnDisabled = useMemo(() => {
    return !(allFieldsAreFilled && fieldsHaveChanged && isInAgeRange);
  }, [allFieldsAreFilled, fieldsHaveChanged, isInAgeRange]);

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      const locationName =
        regions.filter((r) => r.value === region)[0]?.label || region;

      const userInfo = {
        name,
        birthday: new Date(year, month, day),
        location: Array.isArray(locationName) ? locationName[0] : locationName,
        gender: selectedGender,
      };

      try {
        await updateUserInfo(userInfo);
      } catch (e) {
        devWarn(e);
      } finally {
        //callback displaying a sucessfuly save using a snackbar
        setSnackbarText([
          "common:account.personalInfoUpdated",
          "Personal info updated",
        ]);
        close();
      }
    },
    [
      year,
      month,
      day,
      region,
      regions,
      selectedGender,
      name,
      close,
      setSnackbarText,
    ],
  );

  return (
    <DialogWindow minWidth={400} maxWidth={500}>
      <DialogContent>
        <DialogHeader
          Icon={<MessageIcon />}
          title={t(
            "common:account.updateAccountInformation",
            "Update Account Information",
          )}
        />

        <Form onSubmit={onSubmit}>
          <DialogBody>
            <div className="edit-info">
              <div className="dob">
                <div className="type-caption dob-label">
                  {t("common:signup.birthDate", "Date of Birth")}
                </div>
                <div className="dob-fields">
                  <Dropdown
                    name="month"
                    autoComplete="bday-month"
                    required
                    placeholder={t("common:signup.keys.month", "Month")}
                    items={months}
                    value={month}
                    onChange={setMonth}
                    error={showError}
                    block
                  />
                  <Dropdown
                    name="day"
                    autoComplete="bday-day"
                    required
                    placeholder={t("common:signup.keys.day", "Day")}
                    items={days}
                    value={day}
                    onChange={setDay}
                    error={showError}
                    block
                  />
                  <Dropdown
                    name="year"
                    autoComplete="bday-year"
                    required
                    placeholder={t("common:signup.keys.year", "Year")}
                    items={years}
                    value={year}
                    onChange={setYear}
                    error={showError}
                    block
                  />
                </div>

                <div className="relative">
                  <div
                    role="alert"
                    {...classNames(cssInputError(), ageError ? "shown" : "")}
                    aria-hidden
                  >
                    {ageError}
                  </div>
                </div>
              </div>
              <div className="location-gender">
                <div className="location">
                  <div className="type-caption location-label">
                    {t("common:signup.keys.location", "Location")}
                  </div>
                  <Dropdown
                    id="region"
                    name="region"
                    autoComplete="country"
                    required
                    placeholder={t("common:pleaseSelect", "Please select")}
                    search
                    maxHeight={400}
                    items={regions}
                    value={region}
                    onChange={setRegion}
                    className="location-field"
                    block
                  />
                </div>
                <div className="gender">
                  <label className="type-caption gender-label" htmlFor="gender">
                    {t("common:account.gender", "Gender")}
                  </label>
                  <Dropdown
                    id="gender"
                    name="gender"
                    autoComplete="sex"
                    required
                    placeholder={t(
                      "common:onboarding.gender.placeholder",
                      "Please select",
                    )}
                    items={genderOptions}
                    value={selectedGender}
                    onChange={setGender}
                    className="gender-field"
                    block
                  />
                </div>
              </div>
            </div>
          </DialogBody>

          <DialogFooter>
            <Button
              type="cancel"
              emphasis="low"
              size="large"
              onClick={() => close(null)}
            >
              {t("common:cancel", "Cancel")}
            </Button>

            <Button
              type="submit"
              emphasis="high"
              size="large"
              disabled={btnDisabled}
            >
              {t("common:settings.confirm", "Confirm")}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </DialogWindow>
  );
};

export default EditUserSettingsModal;
